<template>
  <v-card
    class="map-tile-layer-control"
    elevation="4"
    :class="{ 'is-expanded': mapExpanded }"
  >
    <v-card-title class="d-flex align-center pa-2" style="min-height: 40px">
      <v-icon size="20" class="mr-2">mdi-map</v-icon>
      <span class="text-subtitle-2">Map Style</span>
    </v-card-title>

    <v-card-text class="pa-2 pt-0">
      <v-radio-group
        v-model="selectedTileLayer"
        density="compact"
        hide-details
        @update:model-value="onTileLayerChange"
      >
        <v-radio
          v-for="tile in tileLayers"
          :key="tile.id"
          :value="tile.id"
          :label="tile.name"
          color="primary"
          density="compact"
        >
          <template #label>
            <div class="d-flex align-center">
              <v-icon size="18" class="mr-2" :color="tile.color">{{ tile.icon }}</v-icon>
              <span class="text-caption">{{ tile.name }}</span>
            </div>
          </template>
        </v-radio>
      </v-radio-group>
    </v-card-text>
  </v-card>
</template>

<script>
import { getLookupDataByType } from "@/api/lookups";

export default {
  name: "MapTileLayerControl",
  props: {
    mapExpanded: {
      type: Boolean,
      default: false,
    },
    currentTileLayer: {
      type: String,
      default: "osm",
    },
  },
  emits: ["update:tile-layer"],
  data() {
    const allTileLayers = [
      {
        id: "osm",
        name: "Street Map",
        mapType: "street",
        icon: "mdi-map",
        color: "primary",
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
      },
      {
        id: "satellite",
        name: "Satellite",
        mapType: "satellite",
        icon: "mdi-satellite-variant",
        color: "success",
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        attribution: "Tiles © Esri",
      },
      {
        id: "terrain",
        name: "Terrain",
        mapType: "terrain",
        icon: "mdi-terrain",
        color: "orange",
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
        attribution: "Tiles © Esri",
      },
    ];
    
    return {
      selectedTileLayer: this.currentTileLayer,
      overlaysLoaded: false,
      allTileLayers: allTileLayers,
      // Initialize with all layers as fallback, will be filtered once API loads
      tileLayers: [...allTileLayers],
    };
  },
  watch: {
    currentTileLayer(newVal) {
      this.selectedTileLayer = newVal;
    },
  },
  mounted() {
    this.loadOverlays();
    // Listen for overlay updates from MapOverlaysToggle using window events
    window.addEventListener('map-overlays-updated', this.loadOverlays);
  },
  beforeUnmount() {
    window.removeEventListener('map-overlays-updated', this.loadOverlays);
  },
  methods: {
    async loadOverlays() {
      try {
        const overlayData = await getLookupDataByType("MapOverlay");
        
        // If no overlay data, keep all layers as fallback
        if (!overlayData || overlayData.length === 0) {
          this.tileLayers = [...this.allTileLayers];
          this.overlaysLoaded = true;
          return;
        }
        
        // Include ALL overlays (active + inactive) so disabled ones stay on screen and can be re-enabled
        const overlayNames = overlayData.map(o => o.name?.toLowerCase().trim() || '');
        
        const filteredLayers = this.allTileLayers.filter(tile => {
          const tileName = (tile.name || '').toLowerCase().trim();
          return overlayNames.includes(tileName);
        });
        
        const layersToUse = filteredLayers.length > 0 ? filteredLayers : [...this.allTileLayers];
        
        layersToUse.sort((a, b) => {
          const aOverlay = overlayData.find(o => 
            (o.name || '').toLowerCase().trim() === (a.name || '').toLowerCase().trim()
          );
          const bOverlay = overlayData.find(o => 
            (o.name || '').toLowerCase().trim() === (b.name || '').toLowerCase().trim()
          );
          const aOrder = aOverlay?.displayOrder ?? 999;
          const bOrder = bOverlay?.displayOrder ?? 999;
          return aOrder - bOrder;
        });
        
        this.tileLayers = layersToUse;
        
        // Keep current layer on screen even if disabled in utils - don't switch away
        const currentLayerAvailable = this.tileLayers.some(t => t.id === this.selectedTileLayer);
        if (!currentLayerAvailable && this.tileLayers.length > 0) {
          this.selectedTileLayer = this.tileLayers[0].id;
          this.onTileLayerChange();
        }
        
        this.overlaysLoaded = true;
      } catch (error) {
        // Fallback to all layers if API fails
        this.tileLayers = [...this.allTileLayers];
        this.overlaysLoaded = true;
      }
    },
    onTileLayerChange() {
      const selected = this.tileLayers.find((t) => t.id === this.selectedTileLayer);
      if (selected) {
        this.$emit("update:tile-layer", selected);
      }
    },
  },
};
</script>

<style scoped>
.map-tile-layer-control {
  position: absolute;
  top: 300px; /* Position below the layer control panel */
  right: 10px;
  left: auto;
  z-index: 1000;
  max-width: 250px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
}

.map-tile-layer-control.is-expanded {
  max-width: 300px;
}

:deep(.v-radio-group) {
  padding: 0;
}

:deep(.v-radio) {
  margin-bottom: 4px;
}

:deep(.v-radio .v-label) {
  font-size: 0.875rem;
  opacity: 1;
}
</style>

